import { useTheme } from '@mui/material/styles';
import { DataTestId } from 'core/common/constants';
import { useConfig } from 'core/common/hooks';
import { useFlow } from 'core/funnel/hooks';
import {
  CardPaymentEntities,
  PaymentError,
  PaymentFormMerchantData,
  PaymentOrderDescription,
  PaymentType,
  PixVariant,
} from 'core/payments/entities';
import { getPixContainerId } from 'core/payments/utils';
import PaymentCard, {
  ApplePayWrapper,
  getDefaultParams,
  getLegalParams,
  GooglePayWrapper,
  PixWrapper,
} from '../PaymentCard';
import { PaymentPaypalStyled, WrapperStyled } from './styled';

type PaymentMethodsStackedProps = {
  shouldRenderPaypal: boolean;
  shouldRenderCardPayment: boolean;
  shouldRenderApplePay: boolean;
  shouldRenderGooglePay: boolean;
  shouldRenderPix: boolean;
  pixVariant: PixVariant;
  formMerchantData: PaymentFormMerchantData;
  paypalPaymentUrl: string;
  onPaymentClick: (paymentType: PaymentType) => void;
  onFormFailed: (data: PaymentError) => void;
  onCardFormError: (data: unknown) => void;
  onVerify: () => void;
  onOrderPaypalStartProcessing: () => void;
  onOrderCardStartProcessing: (paymentType: CardPaymentEntities) => void;
  onPaypalFailed: (data: PaymentError) => void;
  onSucceed: (data: { order?: PaymentOrderDescription }) => void;
};

export const PaymentMethodsStacked = ({
  shouldRenderPaypal,
  shouldRenderCardPayment,
  shouldRenderApplePay,
  shouldRenderGooglePay,
  shouldRenderPix,
  pixVariant,
  formMerchantData,
  paypalPaymentUrl,
  onPaymentClick,
  onFormFailed,
  onCardFormError,
  onVerify,
  onOrderPaypalStartProcessing,
  onOrderCardStartProcessing,
  onPaypalFailed,
  onSucceed,
}: PaymentMethodsStackedProps) => {
  const config = useConfig();
  const { palette } = useTheme();
  const { activeFlow } = useFlow();

  const params =
    activeFlow && config.legalFlows.includes(activeFlow.name)
      ? getLegalParams(palette.payment)
      : getDefaultParams(palette.payment, {
          formParams: {
            submitButtonText: 'Pay',
          },
        });

  return (
    <WrapperStyled>
      {shouldRenderPaypal && (
        <PaymentPaypalStyled
          url={paypalPaymentUrl}
          onFailed={onPaypalFailed}
          onOrderStartProcessing={onOrderPaypalStartProcessing}
          onSucceed={onSucceed}
        />
      )}
      {shouldRenderApplePay && (
        <ApplePayWrapper
          onClick={() => onPaymentClick(PaymentType.APPLE_PAY)}
          id={config.applePayContainerId}
          data-testid={DataTestId.APPLE_PAY_BUTTON}
        />
      )}
      {shouldRenderGooglePay && (
        <GooglePayWrapper
          onClick={() => onPaymentClick(PaymentType.GOOGLE_PAY)}
          id={config.googlePayContainerId}
          data-testid={DataTestId.GOOGLE_PAY_BUTTON}
        />
      )}
      {shouldRenderPix && (
        <PixWrapper
          onClick={() => onPaymentClick(PaymentType.PIX)}
          id={getPixContainerId(pixVariant)}
        />
      )}
      {shouldRenderCardPayment && (
        <PaymentCard
          formMerchantData={formMerchantData}
          onFailed={onFormFailed}
          onError={onCardFormError}
          onVerify={onVerify}
          onSubmitted={onOrderCardStartProcessing}
          onSucceed={onSucceed}
          shouldRenderGooglePay={shouldRenderGooglePay}
          shouldRenderPix={shouldRenderPix}
          pixVariant={pixVariant}
          params={params}
        />
      )}
    </WrapperStyled>
  );
};
